import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useEntitlements } from "@/context/EntitlementContext";
import FeatureLocked from "@/components/FeatureLocked";
import { toast } from "sonner";

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chain, setChain] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const { hasFeature, loading: entLoading } = useEntitlements();

  const load = async () => {
    try {
      const { data } = await api.get("/audit");
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.get("/audit/verify");
      setChain(data);
      toast[data.valid ? "success" : "error"](data.valid ? `Chain valid · ${data.entries} entries` : `Chain broken at #${data.broken_at_seq}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVerifying(false);
    }
  };

  if (!entLoading && !hasFeature("governance_audit")) {
    return <div className="mx-auto max-w-xl py-10" data-testid="audit-locked"><FeatureLocked featureKey="governance_audit" /></div>;
  }
  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Governance</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Audit Log</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Append-only, hash-chained history. Records cannot be edited or deleted via any API.</p>
        </div>
        <button onClick={verify} disabled={verifying} data-testid="verify-chain-button"
          className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-ink-line dark:text-slate-200 dark:hover:bg-ink-panel">
          {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Verify integrity
        </button>
      </div>

      {chain && (
        <div className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${chain.valid ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300"}`} data-testid="chain-status">
          {chain.valid ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
          {chain.valid ? `Integrity verified — ${chain.entries} immutable entries` : `Tampering detected at sequence #${chain.broken_at_seq}`}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 dark:border-ink-line dark:bg-ink/40">
                <th className="px-4 py-3 font-medium">#</th>
                <th className="px-4 py-3 font-medium">Event</th>
                <th className="px-4 py-3 font-medium">Target</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Time</th>
                <th className="px-4 py-3 font-medium">Hash</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.seq} className="border-b border-slate-100 last:border-0 dark:border-ink-line" data-testid={`audit-row-${r.seq}`}>
                  <td className="px-4 py-2.5 font-mono-tf text-xs text-slate-400">{r.seq}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-ink-text">{r.event_type}</td>
                  <td className="px-4 py-2.5 text-slate-500">{r.target_entity_type || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-ink-panel dark:text-slate-300">{r.status || "—"}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400">{new Date(r.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-2.5 font-mono-tf text-[10px] text-slate-400">{r.entry_hash?.slice(0, 12)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
